import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatDate, formatTime } from "@/lib/api";
import { Section, Eyebrow } from "@/components/Section";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  CalendarClock,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock3,
  Trash2,
  AlertTriangle,
} from "lucide-react";

const STATUS_STYLES = {
  pending: { label: "Pending", classes: "bg-[#FAF3E5] text-[#8A6B2F] border-[#EBDDB4]" },
  confirmed: { label: "Confirmed", classes: "bg-[#E8F0EA] text-[#3F6B4E] border-[#C8DBCC]" },
  cancelled: { label: "Cancelled", classes: "bg-[#FBEDEB] text-[#7C4A45] border-[#E8C4BF]" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-body uppercase tracking-[0.16em] ${s.classes}`}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value, hint, testId }) {
  return (
    <div className="bg-white border border-[#E8E5DF] rounded-[1.5rem] p-6" data-testid={testId}>
      <p className="eyebrow text-[10px]">{label}</p>
      <p className="font-serif-display text-4xl mt-3 text-[#2E3630]">{value}</p>
      {hint && <p className="font-body text-xs text-[#5F6B62] mt-2">{hint}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, c, s] = await Promise.all([
        api.get("/bookings"),
        api.get("/contact"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setInquiries(c.data);
      setStats(s.data);
    } catch (e) {
      setError("Couldn't load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/bookings/${id}/status`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      toast.success(`Booking ${status}`);
    } catch (e) {
      toast.error("Couldn't update status.");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Inquiry deleted.");
    } catch (e) {
      toast.error("Couldn't delete inquiry.");
    }
  };

  return (
    <Section testId="admin-dashboard" className="!pt-20 !pb-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Dashboard</Eyebrow>
          <h1 className="font-serif-display text-3xl sm:text-5xl mt-4 text-[#2E3630] leading-[1.05]">
            Welcome back, {admin?.username || "Jeremy"}.
          </h1>
          <p className="font-body mt-3 text-[#5F6B62]">
            Quiet overview of incoming requests.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="btn-pill btn-outline" data-testid="admin-view-site">View site</Link>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout"
            className="btn-pill btn-outline"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testId="stat-bookings-total" label="All bookings" value={stats?.bookings_total ?? "—"} />
        <StatCard testId="stat-bookings-pending" label="Pending review" value={stats?.bookings_pending ?? "—"} />
        <StatCard testId="stat-bookings-upcoming" label="Next 14 days" value={stats?.bookings_upcoming_14d ?? "—"} />
        <StatCard testId="stat-inquiries-total" label="Inquiries" value={stats?.inquiries_total ?? "—"} />
      </div>

      {stats && !stats.email_notifications_enabled && (
        <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#FAF3E5] border border-[#EBDDB4] p-5" data-testid="admin-email-disabled">
          <AlertTriangle size={18} className="text-[#8A6B2F] mt-0.5 shrink-0" />
          <p className="text-sm font-body text-[#5F6B62] leading-relaxed">
            Email notifications are <span className="text-[#2E3630]">off</span>.
            Add a <code className="px-1.5 py-0.5 bg-white border border-[#E8E5DF] rounded text-xs">RESEND_API_KEY</code> in
            <code className="px-1.5 py-0.5 bg-white border border-[#E8E5DF] rounded text-xs">/app/backend/.env</code> and
            restart the backend to start receiving emails when new bookings or inquiries arrive.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-10 flex items-center gap-2 border-b border-[#E8E5DF]" role="tablist" data-testid="admin-tabs">
        {[
          { id: "bookings", label: `Bookings · ${bookings.length}`, icon: CalendarClock },
          { id: "inquiries", label: `Inquiries · ${inquiries.length}`, icon: MessageSquare },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`relative inline-flex items-center gap-2 px-5 py-3 text-sm font-body transition-colors ${
              tab === id ? "text-[#2E3630]" : "text-[#9AA39C] hover:text-[#5F6B62]"
            }`}
          >
            <Icon size={15} /> {label}
            {tab === id && (
              <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-[#6B8E7B] rounded-full" />
            )}
          </button>
        ))}
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-[#5F6B62]" data-testid="admin-loading">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}
      {error && <p className="mt-8 text-[#B06E66] font-body">{error}</p>}

      {!loading && !error && tab === "bookings" && (
        <div className="mt-10 space-y-4" data-testid="admin-bookings-list">
          {bookings.length === 0 && <p className="font-body text-[#5F6B62]">No bookings yet.</p>}
          {bookings.map((b) => (
            <article
              key={b.id}
              data-testid={`admin-booking-${b.id}`}
              className="bg-white border border-[#E8E5DF] rounded-[1.5rem] p-6 sm:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif-display text-2xl text-[#2E3630]">{b.name}</p>
                    <StatusPill status={b.status} />
                    {b.is_veteran && (
                      <span className="inline-flex items-center rounded-full border border-[#6B8E7B] text-[#6B8E7B] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] font-body">
                        Veteran
                      </span>
                    )}
                  </div>
                  <p className="font-body text-sm text-[#5F6B62] flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {b.email}</span>
                    <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {b.phone}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="eyebrow text-[10px]">Session</p>
                  <p className="font-serif-display text-xl text-[#2E3630] mt-1">{formatDate(b.date)}</p>
                  <p className="font-body text-sm text-[#5F6B62]">
                    {formatTime(b.time)} · {b.session_length} min · {b.service}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid sm:grid-cols-3 gap-x-8 gap-y-3 font-body text-sm text-[#5F6B62] border-t border-[#E8E5DF] pt-5">
                <div>
                  <p className="eyebrow text-[10px]">Age range</p>
                  <p className="mt-1 text-[#2E3630]">{b.age_range}</p>
                </div>
                <div>
                  <p className="eyebrow text-[10px]">Concerns</p>
                  <p className="mt-1 text-[#2E3630]">{(b.concerns || []).join(", ") || "—"}</p>
                </div>
                <div>
                  <p className="eyebrow text-[10px]">Submitted</p>
                  <p className="mt-1 text-[#2E3630]">{new Date(b.created_at).toLocaleString()}</p>
                </div>
                {b.notes && (
                  <div className="sm:col-span-3">
                    <p className="eyebrow text-[10px]">Notes</p>
                    <p className="mt-1 text-[#2E3630] whitespace-pre-wrap">{b.notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap gap-2 border-t border-[#E8E5DF] pt-5">
                <button
                  type="button"
                  onClick={() => updateStatus(b.id, "confirmed")}
                  disabled={b.status === "confirmed"}
                  data-testid={`admin-booking-confirm-${b.id}`}
                  className="btn-pill btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 size={14} /> Confirm
                </button>
                <button
                  type="button"
                  onClick={() => updateStatus(b.id, "pending")}
                  disabled={b.status === "pending"}
                  data-testid={`admin-booking-pending-${b.id}`}
                  className="btn-pill btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Clock3 size={14} /> Mark pending
                </button>
                <button
                  type="button"
                  onClick={() => updateStatus(b.id, "cancelled")}
                  disabled={b.status === "cancelled"}
                  data-testid={`admin-booking-cancel-${b.id}`}
                  className="btn-pill btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle size={14} /> Cancel
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && !error && tab === "inquiries" && (
        <div className="mt-10 space-y-4" data-testid="admin-inquiries-list">
          {inquiries.length === 0 && <p className="font-body text-[#5F6B62]">No inquiries yet.</p>}
          {inquiries.map((c) => (
            <article
              key={c.id}
              data-testid={`admin-inquiry-${c.id}`}
              className="bg-white border border-[#E8E5DF] rounded-[1.5rem] p-6 sm:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif-display text-2xl text-[#2E3630]">{c.name}</p>
                  <p className="font-body text-sm text-[#5F6B62] flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-[#6B8E7B]"><Mail size={12} /> {c.email}</a>
                    {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#6B8E7B]"><Phone size={12} /> {c.phone}</a>}
                  </p>
                </div>
                <p className="font-body text-xs text-[#9AA39C]">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <p className="mt-5 font-body text-[#2E3630] whitespace-pre-wrap leading-relaxed">{c.message}</p>
              <div className="mt-5 border-t border-[#E8E5DF] pt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => deleteInquiry(c.id)}
                  data-testid={`admin-inquiry-delete-${c.id}`}
                  className="btn-pill btn-outline text-[#B06E66] border-[#E8C4BF] hover:bg-[#FBEDEB]"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}
